/**
 * Submission Triage — active filters bar (OR-979).
 *
 * Renders under the toolbar when any filter is active: a result count, a
 * removable chip per active filter clause, and "Clear All". Reads the
 * committed `filters`; removing a chip commits the reduced filter set.
 */
import { XClose } from "@untitledui/icons";
import { Tooltip, TooltipTrigger, cx } from "@tricura/atlas-ui";
import { RENEWAL_PRESET_LABELS, type TriageFilters, countActiveFilters } from "./filters-drawer";
import type { SavedView } from "./view-preference-config";

interface ActiveFiltersBarProps {
    filters: TriageFilters;
    /** Total rows matching the committed filters. */
    total: number;
    onChange: (next: TriageFilters) => void;
    onClearAll: () => void;
    /** Saved views currently applied, in application order. Rendered
     *  ahead of the ad-hoc filter chips (design OR-1507). */
    appliedViews?: SavedView[];
    onRemoveView?: (id: string) => void;
}

interface Chip {
    key: string;
    label: string;
    next: TriageFilters;
}

function rangeLabel(min: string, max: string, prefix = ""): string {
    if (min && max) return `${prefix}${min} – ${prefix}${max}`;
    if (min) return `≥ ${prefix}${min}`;
    return `≤ ${prefix}${max}`;
}

/** ISO "2026-01-01" → "01/01/2026"; "" → "Any". */
function dateLabel(iso: string): string {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : "Any";
}

function buildChips(f: TriageFilters): Chip[] {
    const chips: Chip[] = [];
    for (const t of f.types) {
        chips.push({ key: `type:${t}`, label: `Submission Type: ${t}`, next: { ...f, types: f.types.filter((x) => x !== t) } });
    }
    for (const st of f.statuses) {
        chips.push({ key: `status:${st}`, label: `Status: ${st}`, next: { ...f, statuses: f.statuses.filter((x) => x !== st) } });
    }
    if (f.brokerage) chips.push({ key: "brokerage", label: `Brokerage: ${f.brokerage.label}`, next: { ...f, brokerage: null } });
    if (f.renewalPreset) {
        chips.push({ key: "renewal", label: `Renewal Date: ${RENEWAL_PRESET_LABELS[f.renewalPreset]}`, next: { ...f, renewalPreset: "" } });
    } else if (f.renewalStart || f.renewalEnd) {
        chips.push({
            key: "renewal",
            label: `Renewal Date: ${dateLabel(f.renewalStart)} - ${dateLabel(f.renewalEnd)}`,
            next: { ...f, renewalStart: "", renewalEnd: "" },
        });
    }
    if (f.uw) chips.push({ key: "uw", label: `UW: ${f.uw.label}`, next: { ...f, uw: null } });
    if (f.analyst) chips.push({ key: "analyst", label: `Analyst: ${f.analyst.label}`, next: { ...f, analyst: null } });
    if (f.facilityCountMin || f.facilityCountMax) {
        chips.push({
            key: "facilityCount",
            label: `#Communities: ${rangeLabel(f.facilityCountMin, f.facilityCountMax)}`,
            next: { ...f, facilityCountMin: "", facilityCountMax: "" },
        });
    }
    if (f.expiringDeductibleMin || f.expiringDeductibleMax) {
        chips.push({
            key: "expiringDeductible",
            label: `Expiring Deductible: ${rangeLabel(f.expiringDeductibleMin, f.expiringDeductibleMax, "$")}`,
            next: { ...f, expiringDeductibleMin: "", expiringDeductibleMax: "" },
        });
    }
    return chips;
}

/** One-line summary of what a saved view filters on, shown when the
 *  user hovers its chip. Reuses the chip vocabulary so the tooltip and
 *  the bar name each clause the same way. */
function describeFilters(f: TriageFilters): string {
    const labels = buildChips(f).map((c) => c.label);
    return labels.length > 0 ? labels.join(", ") : "No filters";
}

/** The design's Tag/sm: 24px tall, `radius-sm` (4px) — a rounded
 *  rectangle, not a pill — 1px `border-primary`, and asymmetric padding
 *  (spacing-md left, spacing-xs right) because the close glyph sits on
 *  the right. */
const CHIP =
    "border-primary bg-primary text-secondary hover:bg-secondary inline-flex h-6 items-center gap-[3px] rounded-sm border pl-2 pr-1 text-xs font-medium transition duration-100 ease-linear";

export function ActiveFiltersBar({
    filters,
    total,
    onChange,
    onClearAll,
    appliedViews = [],
    onRemoveView,
}: ActiveFiltersBarProps) {
    if (countActiveFilters(filters) === 0 && appliedViews.length === 0) return null;
    const chips = buildChips(filters);

    return (
        <div className="border-secondary flex flex-wrap items-center gap-2 border-b px-4 py-3">
            <span className="text-tertiary text-sm">
                Showing <span className="text-primary font-semibold">{total}</span> results
            </span>
            {appliedViews.map((view) => (
                // Hovering the chip reveals what the view actually filters
                // on, so a name like "High Priority" stays inspectable.
                <Tooltip key={view.id} title={describeFilters(view.filters)} placement="bottom">
                    <TooltipTrigger
                        onPress={() => onRemoveView?.(view.id)}
                        className={cx(CHIP, "max-w-72 cursor-pointer")}
                        data-autotest="triage-chip-saved-view"
                    >
                        <span className="truncate">Saved view: {view.name}</span>
                        <XClose aria-hidden="true" className="text-fg-quaternary size-3.5 shrink-0" />
                    </TooltipTrigger>
                </Tooltip>
            ))}
            {chips.map((chip) => (
                <button
                    key={chip.key}
                    type="button"
                    onClick={() => onChange(chip.next)}
                    className={cx(CHIP, "cursor-pointer")}
                >
                    {chip.label}
                    <XClose aria-hidden="true" className="text-fg-quaternary size-3.5" />
                </button>
            ))}
            <button
                type="button"
                onClick={onClearAll}
                className="text-tertiary hover:text-primary ml-1 cursor-pointer text-xs font-semibold"
            >
                Clear All
            </button>
        </div>
    );
}
